import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { push } from 'react-router-redux';
import { ApiRequestError, ApiServerError, unwrapFetch2 } from '~/fetch';
import { LocalStorage } from '~/helpers';
import type { IToastMessage } from '~/helpers/Toastr';
import { LoadingStatus } from '~/Slices/schema';
import * as SuppliersSlice from '~/Slices/SuppliersSlice';
import type { IAppState } from '~/appstate';
import { toastr } from '../helpers/Toastr';
import * as Hub from '../ogm-hub';

const HARD_CODED_SYSCO_SUPPLIER_ID = 1;

function isValidItemNumber(str: string) {
	const ASCII_0 = 48;
	const ASCII_9 = 57;
	const ASCII_A = 65;
	const ASCII_Z = 90;
	const ASCII_a = 97;
	const ASCII_z = 122;
	const ASCII_COMMA = 44;
	const ASCII_PERIOD = 46;
	if (!str || str.length <= 0) {
		return false;
	}
	for (let i = 0, len = str.length; i < len; i++) {
		const code = str.charCodeAt(i);
		if (
			!(code >= ASCII_0 && code <= ASCII_9) &&
			!(code >= ASCII_A && code <= ASCII_Z) &&
			!(code >= ASCII_a && code <= ASCII_z) &&
			!(code >= ASCII_COMMA && code <= ASCII_PERIOD)
		) {
			return false;
		}
	}
	return true;
}

export const onSupcLookpSave = createAsyncThunk<
	void,
	null,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'customerItemNumbersPage/onSupcLookpSave',
	async (_, { rejectWithValue, getState }) => {
		const supplierId = HARD_CODED_SYSCO_SUPPLIER_ID;
		const state = getState();
		const successToast = {
			title: 'Success',
			body: 'Update will be applied to OVR Charts',
		};
		const errorToast = {
			title: 'Error!',
			body: 'Failed to save. Please try again.',
		};

		const { newCustomerItemNumber: customerItemNumber, supplierProductNumber } =
			state.customerItemNumbersPage;

		const item = {
			supplierProductNumber,
			customerItemNumber,
		};
		try {
			const res = await unwrapFetch2(
				Hub.CustomerItemNumbersController.UpsertCustomerItemNumbers({
					supplierId,
					customerItemNumbers: [item],
				})
			);
			if (res.errors.length === 0) {
				toastr.success(successToast);
			} else {
				toastr.error(errorToast);
				return rejectWithValue(res.errors);
			}
		} catch (err) {
			if (err instanceof ApiRequestError) {
				toastr.error(errorToast);
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				toastr.error(errorToast);
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const onSupcSearch = createAsyncThunk<
	Hub.GetCustomerItemNumberInfoResult,
	null,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'customerItemNumbersPage/onSupcSearch',
	async (_, { rejectWithValue, getState, dispatch }) => {
		const supplierId = HARD_CODED_SYSCO_SUPPLIER_ID;
		const state = getState();
		const { searchTerm } = state.customerItemNumbersPage;

		if (searchTerm.length !== 7) {
			rejectWithValue(['Product Number is incorrect length']);
		}
		const res = await unwrapFetch2(
			Hub.CustomerItemNumbersController.GetCustomerItemNumberInfo({
				supplierId,
				supplierProductNumber: searchTerm,
			})
		);
		if (res.errors.length === 0) {
			dispatch(
				customerItemNumbersPageSlice.actions.customerItemNumberChange(
					res.customerItemNumber
				)
			);
			return res;
		} else {
			return rejectWithValue(res.errors);
		}
	}
);

export const getSuppliers = createAsyncThunk<
	void,
	void,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'customerItemNumbersPage/getSuppliers',
	async (_: void, { rejectWithValue, dispatch }) => {
		try {
			await dispatch(SuppliersSlice.fetchCurrentCustomerSuppliers());
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const fetchMassImportInProgress = createAsyncThunk<
	boolean,
	void,
	{ rejectValue: string[] | Response }
>(
	'customerItemNumbersPage/fetchMassImportInProgress',
	async (_, { rejectWithValue }) => {
		try {
			const customerId = LocalStorage.getCustomerId();

			const massImportInProgress = await unwrapFetch2(
				Hub.MassImportRequestController.IsMassCustomerItemNumberImportInProgress(
					customerId
				)
			);

			return massImportInProgress;
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export interface SubmitMassCustomerItemNumberImportRequestParams {
	file: File;
	supplierId: number;
	successMessage: IToastMessage;
	failureMessage: IToastMessage;
}
export const submitMassCustomerItemNumberImportRequest = createAsyncThunk<
	void,
	SubmitMassCustomerItemNumberImportRequestParams,
	{ rejectValue: string[] | Response }
>(
	'customerItemNumbersPage/submitMassCustomerItemNumberImportRequest',
	async (
		params: SubmitMassCustomerItemNumberImportRequestParams,
		{ rejectWithValue, dispatch }
	) => {
		try {
			const formData: FormData = new FormData();
			formData.append('file', params.file);
			formData.append('supplierId', params.supplierId.toString());

			await unwrapFetch2(
				Hub.MassImportRequestController.NewMassCustomerItemNumberImportRequest(
					formData
				)
			);

			toastr.success(params.successMessage);
			dispatch(push('/reports'));
		} catch (err) {
			if (err instanceof ApiRequestError) {
				toastr.error(params.failureMessage);
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				toastr.error(params.failureMessage);
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const exportAllMappings = createAsyncThunk<
	void,
	void,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'customerItemNumbersPage/exportAllMappings',
	async (_: void, { dispatch }) => {
		try {
			const response = await unwrapFetch2(
				Hub.ReportController.RequestCustomerItemNumberMappingsReport()
			);
			if (response.errors.length === 0) {
				reportSuccessfullyRequested();
			} else {
				unknownErrorRequestingReport();
			}
			dispatch(push('/reports'));
		} catch (err) {
			if ((err as Error).message.includes('report-in-progress')) {
				reportCurrentlyInProgress();
			} else {
				unknownErrorRequestingReport();
			}
		}
	}
);

function validateSingleCustomerItemNumber(customerItemNumber: string): string {
	if (customerItemNumber == null || customerItemNumber.length === 0) {
		return 'Value Too Short';
	}
	if (customerItemNumber?.length > 14) {
		return 'Value Too Long';
	}
	if (!isValidItemNumber(customerItemNumber)) {
		return 'Value Must Be AlphaNumeric or the Special Characters , . or -';
	}
	return null;
}

const unknownErrorRequestingReport = () => {
	toastr.error({
		title: 'Report Request Failed',
		body: 'The report was not successfully requested.',
	});
};

const reportCurrentlyInProgress = () => {
	toastr.warning({
		title: 'Report Request Failed',
		body: 'Another report request is currently in progress',
	});
};

const reportSuccessfullyRequested = () => {
	toastr.success({
		title: 'Report Request Sent',
		body: 'The report was successfully requested.',
	});
};

export interface ICustomerItemNumbersPageState {
	searchTerm: string;
	searchStatus: LoadingStatus;
	supplierProductNumber: string;
	supcDescription: string;
	isSearchTermValid: boolean;
	originalCustomerItemNumber: string;
	newCustomerItemNumber: string;
	isCustomerItemNumberValid: boolean;
	customerItemNumberError: string;
	isSaving: boolean;
	isMassImportModalOpen: boolean;
	isMassImportSaving: boolean;
	isMassImportInProgress: boolean;
}

const initialPageState: ICustomerItemNumbersPageState = {
	searchTerm: null,
	searchStatus: LoadingStatus.idle,
	supplierProductNumber: null,
	supcDescription: '',
	isSearchTermValid: true,
	originalCustomerItemNumber: null,
	newCustomerItemNumber: '',
	customerItemNumberError: '',
	isCustomerItemNumberValid: true,
	isSaving: false,
	isMassImportModalOpen: false,
	isMassImportSaving: false,
	isMassImportInProgress: true,
};
export const customerItemNumbersPageSlice = createSlice({
	name: 'customerItemNumbersPage',
	initialState: initialPageState,
	reducers: {
		onPageLoad(state) {
			state.isSaving = initialPageState.isSaving;
			state.searchTerm = initialPageState.searchTerm;
			state.searchStatus = initialPageState.searchStatus;
			state.supplierProductNumber = initialPageState.supplierProductNumber;
			state.supcDescription = initialPageState.supcDescription;
			state.isSearchTermValid = initialPageState.isSearchTermValid;
			state.newCustomerItemNumber = initialPageState.newCustomerItemNumber;
			state.isCustomerItemNumberValid =
				initialPageState.isCustomerItemNumberValid;
			state.isMassImportModalOpen = initialPageState.isMassImportModalOpen;
			state.isMassImportSaving = initialPageState.isMassImportSaving;
			state.isMassImportInProgress = initialPageState.isMassImportInProgress;
		},
		searchTermChange(state, action) {
			state.searchTerm = action.payload;
		},
		customerItemNumberChange(state, action) {
			const customerItemNumber = action.payload;
			state.newCustomerItemNumber = customerItemNumber;

			const error = validateSingleCustomerItemNumber(customerItemNumber);
			state.isCustomerItemNumberValid = error == null;
			state.customerItemNumberError = error;
		},
		massImportModalOpened(state) {
			state.isMassImportModalOpen = true;
		},
		massImportModalClosed(state) {
			state.isMassImportModalOpen = false;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(onSupcLookpSave.rejected, (state) => {
			state.isSaving = false;
		});
		builder.addCase(onSupcLookpSave.pending, (state) => {
			state.isSaving = true;
		});
		builder.addCase(onSupcLookpSave.fulfilled, (state) => {
			state.isSaving = false;
			state.searchTerm = initialPageState.searchTerm;
			state.searchStatus = initialPageState.searchStatus;
			state.supplierProductNumber = initialPageState.supplierProductNumber;
			state.supcDescription = initialPageState.supcDescription;
			state.isSearchTermValid = initialPageState.isSearchTermValid;
			state.originalCustomerItemNumber =
				initialPageState.originalCustomerItemNumber;
			state.newCustomerItemNumber = initialPageState.newCustomerItemNumber;
			state.isCustomerItemNumberValid =
				initialPageState.isCustomerItemNumberValid;
			state.customerItemNumberError = initialPageState.customerItemNumberError;
		});
		builder.addCase(onSupcSearch.rejected, (state) => {
			state.searchStatus = LoadingStatus.failure;
			state.isSearchTermValid = false;
			state.supplierProductNumber = initialPageState.supplierProductNumber;
			state.supcDescription = initialPageState.supcDescription;
			state.originalCustomerItemNumber =
				initialPageState.originalCustomerItemNumber;
			state.newCustomerItemNumber = initialPageState.newCustomerItemNumber;
			state.isCustomerItemNumberValid =
				initialPageState.isCustomerItemNumberValid;
			state.customerItemNumberError = initialPageState.customerItemNumberError;
		});
		builder.addCase(onSupcSearch.pending, (state) => {
			state.searchStatus = LoadingStatus.loading;
		});
		builder.addCase(onSupcSearch.fulfilled, (state, action) => {
			const { description, customerItemNumber } = action.payload;

			state.searchStatus = LoadingStatus.success;
			state.isSearchTermValid = description != null;
			state.supplierProductNumber =
				description != null || customerItemNumber != null
					? state.searchTerm
					: null;
			state.supcDescription = description;
			state.originalCustomerItemNumber = customerItemNumber;
			state.newCustomerItemNumber = customerItemNumber;
			if (customerItemNumber !== null) {
				const error = validateSingleCustomerItemNumber(customerItemNumber);
				state.isCustomerItemNumberValid = error == null;
				state.customerItemNumberError = error;
			} else {
				state.isCustomerItemNumberValid = true;
				state.customerItemNumberError = null;
			}
		});
		builder.addCase(
			submitMassCustomerItemNumberImportRequest.pending,
			(state) => {
				state.isMassImportSaving = true;
			}
		);
		builder.addCase(
			submitMassCustomerItemNumberImportRequest.fulfilled,
			(state) => {
				state.isMassImportSaving = false;
			}
		);
		builder.addCase(
			submitMassCustomerItemNumberImportRequest.rejected,
			(state) => {
				state.isMassImportSaving = false;
			}
		);
		builder.addCase(fetchMassImportInProgress.fulfilled, (state, action) => {
			state.isMassImportInProgress = action.payload;
		});
	},
});
